/**
 * Purchase orders, deliveries, and the invoices the club is not paying.
 *
 * ---------------------------------------------------------------------
 * THE HELD INVOICES COME FIRST, AND THE ORDER BOOK IS UNDERNEATH
 *
 * Same argument as the kitchen's below-par list. Nobody opens this
 * screen to browse purchase orders. They open it because a vendor rang
 * about a cheque, or because it is Thursday and the payment run is
 * Friday — and both of those are the held list.
 *
 * An order that arrived and was billed correctly needs no attention from
 * anybody ever again. The whole value of the module is the small number
 * of times it says stop.
 *
 * ---------------------------------------------------------------------
 * THREE FIGURES ON ONE ROW, ALWAYS
 *
 * Ordered, received, billed. Never two of them, and never a single
 * variance number with the workings hidden behind a click.
 *
 * A variance on its own says the invoice is wrong by forty dollars. The
 * three figures say WHICH of the three parties disagrees, which is the
 * only thing that tells somebody what to do next — ring the vendor,
 * check the dock, or fix the order. That is the whole reason the match
 * is three-way rather than two, and a screen that collapses it back to
 * one number has quietly undone the module.
 *
 * ---------------------------------------------------------------------
 * HELD IS NOT AN ERROR STATE, SO IT IS NOT RED
 *
 * The API returns 200 for a held invoice on purpose (see
 * `purchasing.ts`), and this screen carries that through: a hold is
 * amber and it is a queue of work, not a failure. Red here would train
 * people to dismiss it, and the one thing a hold must not be is
 * dismissible.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { money, type CurrencyInfo } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });

interface Order {
  id: string;
  po_number: string;
  status: string;
  ordered_on: string;
  expected_on: string | null;
  vendor: string;
  department: string | null;
  lines: number;
  ordered_minor: string;
  received_minor: string;
  fully_received: boolean;
  overdue: boolean;
}

interface OrderLine {
  id: string;
  line_number: number;
  description: string;
  quantity: string;
  unit_cost_minor: string;
  received_quantity: string;
  billed_quantity: string;
}

interface Receipt {
  id: string;
  received_on: string;
  packing_slip: string | null;
  note: string | null;
}

interface GrniLine {
  purchase_order_line_id: string;
  po_number: string;
  vendor: string;
  description: string;
  ordered_qty: string;
  received_qty: string;
  billed_qty: string;
  unbilled_qty: string;
  unbilled_minor: string;
  last_received_on: string;
  days_outstanding: number;
}

interface HeldInvoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  total_minor: string;
  vendor: string;
  hold_reason: string | null;
  held_by_match: boolean;
  past_due: boolean;
}

interface MatchLine {
  ap_invoice_line_id: string;
  description: string;
  po_number: string;
  ordered_qty: string;
  received_qty: string;
  billed_qty: string;
  ordered_unit_minor: string;
  billed_unit_minor: string | null;
  price_variance_minor: string;
  within_tolerance: boolean;
  problem: string | null;
}

interface Policy {
  price_tolerance_minor: number;
  price_tolerance_bp: number;
  over_receipt_tolerance_bp: number;
  require_receipt: boolean;
}

export function PurchasingPage() {
  const { currency, atLeast } = useSession();
  const [tab, setTab] = useState<'held' | 'orders' | 'grni'>('held');

  const mayMatch = atLeast('controller');

  const policy = useApi<{ policy: Policy }>('/v1/purchasing/policy');
  const orders = useApi<{ orders: Order[] }>('/v1/purchasing/orders');
  const grni = useApi<{ lines: GrniLine[]; total_minor: number }>('/v1/purchasing/grni');
  // Only a controller may read the held list, so a manager is not asked
  // to fetch a 403 and then told it went wrong.
  const held = useApi<{ invoices: HeldInvoice[] }>(
    mayMatch ? '/v1/purchasing/held' : null,
  );

  const orderList = orders.data?.orders ?? [];
  const open = useMemo(() => orderList.filter((o) => o.status === 'open'), [orderList]);
  const overdue = useMemo(() => open.filter((o) => o.overdue), [open]);
  const heldList = held.data?.invoices ?? [];

  if (orders.loading && !orders.data) return <Loading />;

  const p = policy.data?.policy;

  return (
    <>
      <h1>Purchasing</h1>
      <ErrorBanner error={orders.error ?? grni.error ?? held.error} />

      <p className="muted small">
        What the club ordered, what arrived, and what it was billed. A delivery puts the goods on
        the shelf and the money into goods received not invoiced; the vendor&rsquo;s invoice
        clears it again. Where the three do not agree, the invoice is held — recorded and posted,
        because the debt is real, but left out of the payment run until somebody looks at it.
      </p>

      <div className="tiles">
        <Tile
          label="Held"
          value={String(heldList.length)}
          note={heldList.length > 0 ? 'not being paid' : 'nothing disputed'}
        />
        <Tile
          label="Received, not billed"
          value={money(Number(grni.data?.total_minor ?? 0), currency)}
          note={`${grni.data?.lines.length ?? 0} line(s)`}
        />
        <Tile label="Open orders" value={String(open.length)}
              note={overdue.length > 0 ? `${overdue.length} overdue` : 'none overdue'} />
      </div>

      <div className="actions">
        {(['held', 'orders', 'grni'] as const).map((t) => (
          <button
            key={t}
            className={tab === t ? 'small' : 'secondary small'}
            onClick={() => setTab(t)}
          >
            {t === 'held' ? 'Not being paid'
              : t === 'orders' ? 'Orders'
              : 'Received, not billed'}
          </button>
        ))}
      </div>

      {tab === 'held' && (
        mayMatch
          ? <HeldInvoices invoices={heldList} reload={held.reload} currency={currency} />
          : (
            <Card title="Not Being Paid">
              <Empty>
                Deciding whether the club pays a disputed invoice is a controller&rsquo;s.
              </Empty>
            </Card>
          )
      )}

      {tab === 'orders' && (
        <Orders orders={orderList} currency={currency} reload={orders.reload} />
      )}

      {tab === 'grni' && (
        <Card
          title="Received, Not Billed"
          actions={<span className="muted small">
            {money(Number(grni.data?.total_minor ?? 0), currency)}
          </span>}
        >
          <p className="muted small">
            Goods that are on the shelf and on the balance sheet, waiting for the vendor&rsquo;s
            invoice. A few days here is ordinary. Months here is a delivery nobody ever billed
            for, or an invoice somebody coded straight to an expense account instead of matching
            it — which pays for the same goods twice.
          </p>
          {(grni.data?.lines.length ?? 0) === 0 ? (
            <Empty>Everything that has arrived has been billed for.</Empty>
          ) : (
            <div className="table">
              <table className="data data--cards">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>What</th>
                    <th className="num">Received</th>
                    <th className="num">Billed</th>
                    <th className="num">Waiting on</th>
                    <th className="num">Value</th>
                    <th className="num">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {(grni.data?.lines ?? []).map((l) => (
                    <tr key={l.purchase_order_line_id}>
                      <td data-label="Order" className="cell--title">
                        {l.po_number}
                        <span className="muted small"> · {l.vendor}</span>
                      </td>
                      <td data-label="What">{l.description}</td>
                      <td data-label="Received" className="num">
                        {nf.format(Number(l.received_qty))}
                      </td>
                      <td data-label="Billed" className="num muted">
                        {nf.format(Number(l.billed_qty))}
                      </td>
                      <td data-label="Waiting on" className="num">
                        {nf.format(Number(l.unbilled_qty))}
                      </td>
                      <td data-label="Value" className="num">
                        {money(Number(l.unbilled_minor), currency)}
                      </td>
                      <td data-label="Days" className="num">
                        {/* Sixty days is where a club should start
                            asking. Amber rather than red: it is a
                            question, not a fault. */}
                        <Status tone={l.days_outstanding > 60 ? 'warning' : 'neutral'}>
                          {l.days_outstanding}
                        </Status>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {p && (
        <p className="muted small">
          This club treats a price difference as a rounding up to{' '}
          {money(p.price_tolerance_minor, currency)} or {(p.price_tolerance_bp / 100).toFixed(2)}%
          of the line, whichever is larger, and allows{' '}
          {(p.over_receipt_tolerance_bp / 100).toFixed(2)}% over on a delivery.{' '}
          {p.require_receipt
            ? 'Being billed for more than arrived is never within tolerance.'
            : 'Invoices are matched against the order only — nothing checks what arrived.'}
        </p>
      )}
    </>
  );
}

/* ==================================================================== */

function HeldInvoices({ invoices, reload, currency }: {
  invoices: HeldInvoice[]; reload: () => void; currency: CurrencyInfo;
}) {
  // 0041's `term` exists for exactly this word. It is "check" at
  // Rockford and "cheque" in Ontario, and the club's own vocabulary
  // table decides — which is better than either spelling hardcoded, and
  // is why the British-spelling check tells you to reach for it.
  const { term } = useSession();
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card title="Not Being Paid">
      <p className="muted small">
        Each of these is posted and owed. What is stopping them is the {term('check')
          .toLowerCase()}, not the bookkeeping — the payment run leaves them out until
        somebody here decides.
      </p>
      {invoices.length === 0 ? (
        <Empty>Nothing is being held.</Empty>
      ) : (
        <div className="table">
          <table className="data data--cards">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Vendor</th>
                <th className="num">Amount</th>
                <th>Due</th>
                <th>Stopped by</th>
                <th>Why</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td data-label="Invoice" className="cell--title">{i.invoice_number}</td>
                  <td data-label="Vendor">{i.vendor}</td>
                  <td data-label="Amount" className="num">
                    {money(Number(i.total_minor), currency)}
                  </td>
                  <td data-label="Due">
                    <Status tone={i.past_due ? 'warning' : 'neutral'}>{i.due_date}</Status>
                  </td>
                  <td data-label="Stopped by" className="muted small">
                    {/* Worth distinguishing: one of these is a rule and
                        the other is a colleague, and what you do about
                        them is different. */}
                    {i.held_by_match ? 'the match' : 'somebody here'}
                  </td>
                  <td data-label="Why" className="small">{i.hold_reason ?? '—'}</td>
                  <td>
                    <button
                      className="secondary small"
                      onClick={() => setOpen(open === i.id ? null : i.id)}
                    >
                      {open === i.id ? 'Close' : 'Look'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <MatchDetail invoiceId={open} currency={currency} onDone={() => {
        setOpen(null);
        reload();
      }} />}
    </Card>
  );
}

/**
 * The three figures, side by side, and the way past them.
 *
 * The release form is deliberately inside this panel rather than a
 * button on the row above. Releasing a hold is the one action here that
 * costs the club money, and it should not be reachable without the
 * comparison being on screen.
 */
function MatchDetail({ invoiceId, currency, onDone }: {
  invoiceId: string; currency: CurrencyInfo; onDone: () => void;
}) {
  const detail = useApi<{ lines: MatchLine[]; matched: boolean }>(
    `/v1/purchasing/invoices/${invoiceId}/match`, [invoiceId],
  );
  const [reason, setReason] = useState('');

  const release = useMutation(async () => {
    await api(`/v1/purchasing/invoices/${invoiceId}/release`, {
      method: 'POST',
      body: { reason },
    });
    onDone();
  });

  if (detail.loading && !detail.data) return <Loading rows={2} />;

  return (
    <div className="panel">
      <ErrorBanner error={detail.error ?? release.error} />
      <div className="table">
        <table className="data data--cards">
          <thead>
            <tr>
              <th>What</th>
              <th>Order</th>
              <th className="num">Ordered</th>
              <th className="num">Received</th>
              <th className="num">Billed</th>
              <th className="num">At</th>
              <th className="num">Billed at</th>
              <th>Problem</th>
            </tr>
          </thead>
          <tbody>
            {(detail.data?.lines ?? []).map((l) => (
              <tr key={l.ap_invoice_line_id}>
                <td data-label="What" className="cell--title">{l.description}</td>
                <td data-label="Order" className="muted small">{l.po_number}</td>
                <td data-label="Ordered" className="num">{nf.format(Number(l.ordered_qty))}</td>
                <td data-label="Received" className="num">{nf.format(Number(l.received_qty))}</td>
                <td data-label="Billed" className="num">{nf.format(Number(l.billed_qty))}</td>
                <td data-label="At" className="num muted">
                  {money(Number(l.ordered_unit_minor), currency)}
                </td>
                <td data-label="Billed at" className="num">
                  {l.billed_unit_minor === null
                    ? '—'
                    : money(Math.round(Number(l.billed_unit_minor)), currency)}
                </td>
                <td data-label="Problem">
                  {l.within_tolerance
                    ? <Status tone="good">agrees</Status>
                    : <Status tone="warning">{l.problem ?? 'does not agree'}</Status>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="form-row">
        <label>
          Pay it anyway, because
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="the vendor's price went up and we agreed"
            maxLength={400}
          />
        </label>
        <button
          disabled={release.busy || reason.trim().length === 0}
          onClick={() => release.run()}
        >
          Release
        </button>
      </div>
      <p className="muted small">
        {/* Not a nag. This sentence is the only reason the reason field
            is required, and somebody typing "ok" into it should read
            why before they do. */}
        This is kept on the invoice and is the club&rsquo;s only record of why it paid a bill
        that did not match its order.
      </p>
    </div>
  );
}

/* ==================================================================== */

function Orders({ orders, currency, reload }: {
  orders: Order[]; currency: CurrencyInfo; reload: () => void;
}) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card title="Orders">
      {orders.length === 0 ? (
        <Empty>Nothing has been ordered yet.</Empty>
      ) : (
        <div className="table">
          <table className="data data--cards">
            <thead>
              <tr>
                <th>Order</th>
                <th>Vendor</th>
                <th>Ordered</th>
                <th>Expected</th>
                <th className="num">Value</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td data-label="Order" className="cell--title">{o.po_number}</td>
                  <td data-label="Vendor">{o.vendor}</td>
                  <td data-label="Ordered" className="muted small">{o.ordered_on}</td>
                  <td data-label="Expected" className="muted small">
                    {o.expected_on ?? '—'}
                  </td>
                  <td data-label="Value" className="num">
                    {money(Number(o.ordered_minor), currency)}
                  </td>
                  <td data-label="Status">
                    <Status
                      tone={
                        o.overdue ? 'warning'
                          : o.status === 'open' ? (o.fully_received ? 'good' : 'neutral')
                          : o.status === 'draft' ? 'neutral'
                          : 'neutral'
                      }
                    >
                      {o.overdue ? 'overdue'
                        : o.status === 'open' && o.fully_received ? 'all in'
                        : o.status}
                    </Status>
                  </td>
                  <td>
                    <button
                      className="secondary small"
                      onClick={() => setOpen(open === o.id ? null : o.id)}
                    >
                      {open === o.id ? 'Close' : 'Lines'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <OrderDetail orderId={open} currency={currency} reload={reload} />}
    </Card>
  );
}

function OrderDetail({ orderId, currency, reload }: {
  orderId: string; currency: CurrencyInfo; reload: () => void;
}) {
  const detail = useApi<{ order: Order; lines: OrderLine[]; receipts: Receipt[] }>(
    `/v1/purchasing/orders/${orderId}`, [orderId],
  );

  const open = useMutation(async () => {
    await api(`/v1/purchasing/orders/${orderId}/open`, { method: 'POST' });
    detail.reload();
    reload();
  });

  if (detail.loading && !detail.data) return <Loading rows={2} />;
  const order = detail.data?.order;

  return (
    <div className="panel">
      <ErrorBanner error={detail.error ?? open.error} />
      <div className="table">
        <table className="data data--cards">
          <thead>
            <tr>
              <th>#</th>
              <th>What</th>
              <th className="num">Ordered</th>
              <th className="num">Received</th>
              <th className="num">Billed</th>
              <th className="num">Each</th>
            </tr>
          </thead>
          <tbody>
            {(detail.data?.lines ?? []).map((l) => (
              <tr key={l.id}>
                <td data-label="#" className="muted small">{l.line_number}</td>
                <td data-label="What" className="cell--title">{l.description}</td>
                <td data-label="Ordered" className="num">{nf.format(Number(l.quantity))}</td>
                <td data-label="Received" className="num">
                  {nf.format(Number(l.received_quantity))}
                </td>
                <td data-label="Billed" className="num muted">
                  {nf.format(Number(l.billed_quantity))}
                </td>
                <td data-label="Each" className="num">
                  {money(Number(l.unit_cost_minor), currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {order?.status === 'draft' && (
        <div className="form-row">
          <button disabled={open.busy} onClick={() => open.run()}>
            Send it to the vendor
          </button>
          <span className="muted small">
            Nothing may be received against a draft, because until it is sent the quantities and
            prices can still be edited to match whatever turns up.
          </span>
        </div>
      )}

      {(detail.data?.receipts.length ?? 0) > 0 && (
        <p className="muted small">
          Delivered{' '}
          {(detail.data?.receipts ?? [])
            .map((r) => `${r.received_on}${r.packing_slip ? ` (${r.packing_slip})` : ''}`)
            .join(', ')}
          .
        </p>
      )}
    </div>
  );
}
